import React, { useEffect, useState } from 'react';
import { Result, Button, Typography, Spin, Steps, Card, Space, Grid } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    LoadingOutlined,
    CreditCardOutlined,
    ShoppingOutlined
} from '@ant-design/icons';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPayment } from './PaymentStatusServices';

const { Title, Text } = Typography;
const { Step } = Steps;

const PaymentStatus = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const [paymentStatus, setPaymentStatus] = useState('checking');
    const [sessionId, setSessionId] = useState('');
    const [paymentDetails, setPaymentDetails] = useState(null);
    const screens = Grid.useBreakpoint();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const sessionIdParam = queryParams.get('payment_id');

        if (sessionIdParam) {
            setSessionId(sessionIdParam);
            fetchPaymentDetails(sessionIdParam);
        } else {
            setPaymentStatus('error');
        }
    }, [location.search]);

    // Responsive handled via Ant Design Grid.useBreakpoint

    const fetchPaymentDetails = async (paymentId) => {
        try {
            const response = await getPayment(paymentId);
            if (response.success) {
                setPaymentDetails(response.data);
                setPaymentStatus('success');
            } else {
                setPaymentStatus('error');
            }
        } catch (error) {
            setPaymentStatus('error');
        }
    };

    const containerStyle = {
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
    };

    const resultStyle = {
        transition: 'all 0.3s ease-in-out',
        borderRadius: screens.xs ? 0 : '16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
        background: 'rgba(255, 255, 255, 0.95)',
        height: screens.xs ? '100%' : 'auto',
        width: screens.xs ? '100%' : '90%',
        maxWidth: '1200px',
        display: screens.xs ? 'flex' : undefined,
        flexDirection: screens.xs ? 'column' : undefined,
        justifyContent: screens.xs ? 'center' : undefined
    };

    const renderContent = () => {
        switch (paymentStatus) {
            case 'success':
                return (
                    <div style={resultStyle}>
                        <Result
                            style={{ padding: screens.xs ? '8px 8px' : undefined, overflowY: screens.xs ? 'auto' : undefined, overflowX: 'hidden', WebkitOverflowScrolling: screens.xs ? 'touch' : undefined, maxHeight: screens.xs ? '100%' : undefined }}
                            icon={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: '64px' }} />}
                            title={
                                <Title level={2} style={{ color: '#2f54eb', margin: 0, lineHeight: 1.2 }}>
                                    {t('payment_successful')} 🎉
                                </Title>
                            }
                            subTitle={
                                <Text type="secondary" style={{ fontSize: '14px', margin: 0 }}>
                                    {t('payment_confirmation_email_sent')}
                                </Text>
                            }
                            extra={[
                                <div key="dashboard-wrap" style={{ marginTop: 4, marginBottom: 4 }}>
                                    <Button
                                        type="primary"
                                        key="dashboard"
                                        size="middle"
                                        style={{ padding: '0 20px', height: '40px' }}
                                    >
                                        <Link to="/">{t('go_to_home')}</Link>
                                    </Button>
                                </div>,
                            ]}
                        >
                            <Space direction="vertical" size={6} style={{ width: '100%', marginTop: 'clamp(4px, 1vh, 12px)' }}>
                                <Steps current={2} size="small" style={{ maxWidth: '520px', margin: '0 auto' }}>
                                    <Step title={t('cart')} icon={<ShoppingOutlined />} />
                                    <Step title={t('payment')} icon={<CreditCardOutlined />} />
                                    <Step title={t('complete')} icon={<CheckCircleOutlined />} />
                                </Steps>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'clamp(6px, 1.5vw, 12px)' }}>
                                    <Card title={t('order_summary')} bordered={false} size="small" headStyle={{ marginBottom: 8 }} bodyStyle={{ padding: 12 }}>
                                        <Space direction="vertical" size={4}>
                                            <Text>{t('order_id')}: #{sessionId}</Text>
                                            <Text>{t('amount')}: ${paymentDetails?.total}</Text>
                                            <Text>{t('date')}: {new Date(paymentDetails?.payment_date).toLocaleDateString()}</Text>
                                        </Space>
                                    </Card>

                                    <Card title={t('billing_information')} bordered={false} size="small" headStyle={{ marginBottom: 8 }} bodyStyle={{ padding: 12 }}>
                                        <Space direction="vertical" size={4}>
                                            <Text>{t('address_line1')}: {paymentDetails?.billing_address?.address_line1}</Text>
                                            <Text>{t('address_line2')}: {paymentDetails?.billing_address?.address_line2}</Text>
                                            <Text>{t('city')}: {paymentDetails?.billing_address?.city}</Text>
                                            <Text>{t('country')}: {paymentDetails?.billing_address?.country}</Text>
                                            <Text>{t('postal_code')}: {paymentDetails?.billing_address?.postal_code}</Text>
                                        </Space>
                                    </Card>
                                </div>
                            </Space>
                        </Result>
                    </div>
                );

            case 'error':
                return (
                    <div style={resultStyle}>
                        <Result
                            style={{ padding: screens.xs ? '8px 8px' : undefined, overflowY: screens.xs ? 'auto' : undefined, overflowX: 'hidden', WebkitOverflowScrolling: screens.xs ? 'touch' : undefined, maxHeight: screens.xs ? '100%' : undefined }}
                            icon={<CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '64px' }} />}
                            title={
                                <Title level={2} style={{ color: '#eb2f2f', margin: 0, lineHeight: 1.2 }}>
                                    {t('payment_failed')} 😢
                                </Title>
                            }
                            subTitle={
                                <Text type="secondary" style={{ fontSize: '14px', margin: 0 }}>
                                    {t('payment_failed_description')}
                                </Text>
                            }
                            extra={[
                                <div key="home-wrap" style={{ marginTop: 4, marginBottom: 4 }}>
                                    <Button
                                        type="primary"
                                        key="home"
                                        size="middle"
                                        style={{ padding: '0 20px', height: '40px' }}
                                    >
                                        <Link to="/">{t('go_to_home')}</Link>
                                    </Button>
                                </div>
                            ]}
                        >
                            <div style={{ marginTop: 'clamp(4px, 1vh, 12px)', textAlign: 'center' }}>
                                <Card title={t('troubleshooting_tips')} bordered={false} size="small" headStyle={{ marginBottom: 8 }} bodyStyle={{ padding: 12 }}>
                                    <Space direction="vertical" align="start" size={4}>
                                        <Text>• {t('check_card_details')}</Text>
                                        <Text>• {t('ensure_sufficient_funds')}</Text>
                                        <Text>• {t('try_different_payment_method')}</Text>
                                        <Text>• {t('contact_bank_for_authorization')}</Text>
                                    </Space>
                                </Card>
                            </div>
                        </Result>
                    </div>
                );

            default:
                return (
                    <div style={{ textAlign: 'center', padding: 'clamp(8px, 2vh, 16px) 0', ...resultStyle }}>
                        <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 56 }} spin />}
                            tip={
                                <Text strong style={{ fontSize: '16px', marginTop: '12px' }}>
                                    {t('processing_payment')}
                                </Text>
                            }
                        />
                        <div style={{ marginTop: '16px' }}>
                            <Text type="secondary">{t('do_not_close_window')}</Text>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div style={containerStyle}>
            {renderContent()}
        </div>
    );
};

export default PaymentStatus;